import { useState } from 'react';
import { useCreateCompany } from '../../api/hooks';
import type { CompanyCreate } from '../../api/schemas';
import styles from './CompanyPickerButton.module.css';

/**
 * Tlačítko „💾 Uložit do adresáře" — založí firmu v DB (/api/companies/ POST).
 * Port z frontend/index.html:20718 (denikSaveToAddrBook).
 */

export interface SaveCompanyButtonProps {
  /** Builder dat — funkce, která vrátí data firmy ze současného stavu formuláře. */
  build: () => CompanyCreate | null;
  label?: string;
  compact?: boolean;
  /** Dovolí uživateli pokračovat dál i při chybě (např. duplicita). */
  title?: string;
}

export function SaveCompanyButton({
  build,
  label = 'Uložit do adresáře',
  compact,
  title,
}: SaveCompanyButtonProps) {
  const create = useCreateCompany();
  const [status, setStatus] = useState<null | { kind: 'ok' | 'err'; msg: string }>(null);

  async function handleClick() {
    const payload = build();
    if (!payload) {
      setStatus({ kind: 'err', msg: 'Vyplňte alespoň název firmy.' });
      window.setTimeout(() => setStatus(null), 3000);
      return;
    }
    if (!payload.nazev.trim()) {
      setStatus({ kind: 'err', msg: 'Vyplňte alespoň název firmy.' });
      window.setTimeout(() => setStatus(null), 3000);
      return;
    }
    try {
      await create.mutateAsync(payload);
      setStatus({ kind: 'ok', msg: `✓ „${payload.nazev}" uloženo` });
      window.setTimeout(() => setStatus(null), 3000);
    } catch (e) {
      setStatus({
        kind: 'err',
        msg: e instanceof Error ? e.message : 'Chyba při ukládání',
      });
      window.setTimeout(() => setStatus(null), 4000);
    }
  }

  const hint = status
    ? status.kind === 'ok'
      ? status.msg
      : `✗ ${status.msg}`
    : title ?? 'Uložit firmu do adresáře';

  return (
    <button
      type="button"
      className={styles.trigger}
      onClick={handleClick}
      disabled={create.isPending}
      title={hint}
      style={
        status
          ? {
              borderColor:
                status.kind === 'ok' ? 'rgba(52,211,153,0.4)' : 'rgba(239,68,68,0.4)',
              color: status.kind === 'ok' ? 'var(--sd-success)' : 'var(--sd-danger)',
            }
          : undefined
      }
    >
      <span aria-hidden>💾</span>
      {!compact && <span>{create.isPending ? 'Ukládám…' : label}</span>}
    </button>
  );
}
